import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper
} from '@mui/material';
import {
  ViewList as ViewListIcon,
  Security as SecurityIcon,
  BarChart as BarChartIcon,
  IntegrationInstructions as IntegrationIcon,
  CheckCircle as CheckCircleIcon,
  Speed as SpeedIcon,
  Shield as ShieldIcon,
  CloudDone as CloudDoneIcon,
  Verified as VerifiedIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

const Capabilities: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <ViewListIcon sx={{ fontSize: 60 }} />,
      color: '#003366',
      title: 'Federal Data Discovery',
      subtitle: 'Data Catalog',
      description: 'FISMA-compliant metadata management with automated security classification and cross-agency data sharing capabilities.',
      benefits: [
        'Automated metadata discovery',
        'Intelligent search across 50+ systems',
        'Business glossary integration',
        'Data lineage visualization',
        'Asset relationship mapping'
      ],
      roi: '$52M annual savings',
      link: '/features/data-catalog'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 60 }} />,
      color: '#B31B1B',
      title: 'Mission-Aligned Governance',
      subtitle: 'Data Governance',
      description: 'NIST 800-53 policy templates, ATO documentation automation, and continuous compliance monitoring for federal agencies.',
      benefits: [
        'Policy management & enforcement',
        'Automated ATO documentation',
        'FISMA & NIST 800-53 compliance',
        'Data stewardship workflows',
        'Quality rules engine'
      ],
      roi: '$60M annual savings',
      link: '/features/data-governance'
    },
    {
      icon: <BarChartIcon sx={{ fontSize: 60 }} />,
      color: '#003366',
      title: 'Intelligence-Grade Analytics',
      subtitle: 'Analytics & Insights',
      description: 'Real-time threat detection dashboards, emergency response coordination, and cross-agency intelligence sharing.',
      benefits: [
        'Pre-built government dashboards',
        'Predictive analytics',
        'Real-time monitoring',
        'Custom report builder',
        'KPI tracking & trends'
      ],
      roi: '$3.18M annual savings',
      link: '/features/analytics'
    },
    {
      icon: <IntegrationIcon sx={{ fontSize: 60 }} />,
      color: '#B31B1B',
      title: 'Secure Government Integration',
      subtitle: 'Enterprise Integration',
      description: 'FedRAMP-authorized connectors, air-gapped deployment options, and DISA STIG compliance for mission-critical systems.',
      benefits: [
        'FedRAMP-authorized connectors',
        'Air-gapped deployment',
        'DISA STIG compliance',
        'Cross-agency data sharing',
        'Real-time synchronization'
      ],
      roi: '$15M annual savings',
      link: '/features/integration'
    }
  ];

  const additionalFeatures = [
    {
      icon: <SpeedIcon sx={{ color: '#003366', fontSize: 40 }} />,
      title: 'High Performance',
      description: '10x faster data discovery with optimized search and caching'
    },
    {
      icon: <ShieldIcon sx={{ color: '#B31B1B', fontSize: 40 }} />,
      title: 'Zero Trust Security',
      description: 'Multi-factor authentication, PIV/CAC support, continuous verification'
    },
    {
      icon: <CloudDoneIcon sx={{ color: '#003366', fontSize: 40 }} />,
      title: 'FedRAMP Ready',
      description: 'Built for FedRAMP High authorization with government-approved cloud services'
    },
    {
      icon: <VerifiedIcon sx={{ color: '#B31B1B', fontSize: 40 }} />,
      title: '508 Compliant',
      description: 'Full Section 508 and WCAG 2.0 Level AA accessibility compliance'
    },
    {
      icon: <TrendingUpIcon sx={{ color: '#003366', fontSize: 40 }} />,
      title: 'Scalable Architecture',
      description: 'Handles millions of data assets across hundreds of systems'
    },
    {
      icon: <CheckCircleIcon sx={{ color: '#B31B1B', fontSize: 40 }} />,
      title: 'No Vendor Lock-In',
      description: 'Open standards, API-first architecture, full data export capabilities'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#ffffff' }}>
      {/* DHS-Style Navigation Header */}
      <Box sx={{ 
        bgcolor: 'white', 
        borderBottom: '1px solid #e0e0e0',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            py: 2
          }}>
            {/* Logo/Brand */}
            <Box
              component="button"
              sx={{ 
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 },
                border: 'none',
                background: 'none',
                padding: 0,
                display: 'flex',
                alignItems: 'center'
              }}
              onClick={() => navigate('/')}
              aria-label="Go to home page"
            >
              <img 
                src="/images/eunify-m.png" 
                alt="E-Unify" 
                style={{ height: 40, width: 'auto', display: 'block' }}
              />
            </Box>

            {/* Navigation Menu */}
            <Box sx={{ 
              display: { xs: 'none', md: 'flex' }, 
              gap: 4,
              alignItems: 'center'
            }}>
              <Typography 
                sx={{ 
                  color: '#0071BC', 
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  textDecoration: 'underline'
                }}
                onClick={() => navigate('/features')}
              >
                Features
              </Typography>
              <Typography 
                sx={{ 
                  color: '#003366', 
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  '&:hover': { color: '#0071BC', textDecoration: 'underline' }
                }}
                onClick={() => navigate('/about')}
              >
                About
              </Typography>
              <Typography 
                sx={{ 
                  color: '#003366', 
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  '&:hover': { color: '#0071BC', textDecoration: 'underline' }
                }}
                onClick={() => navigate('/contact')}
              >
                Contact
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/login')}
                sx={{
                  borderColor: '#003366',
                  color: '#003366',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    borderColor: '#0071BC',
                    bgcolor: 'rgba(0,113,188,0.04)'
                  }
                }}
              >
                Sign In
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate('/register')}
                sx={{
                  bgcolor: '#003366',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    bgcolor: '#0071BC'
                  }
                }}
              >
                Get Started
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Hero Header */}
      <Box sx={{ bgcolor: '#003366', color: 'white', py: 8, pb: 12 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" gutterBottom sx={{ fontWeight: 700, color: 'white' }}>
            Platform Features
          </Typography>
          <Typography variant="h6" align="center" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            Purpose-built capabilities for federal data intelligence
          </Typography>
        </Container>
      </Box>

      {/* Core Features Section - Overlapping Card */}
      <Container maxWidth="lg" sx={{ mt: -8 }}>
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 3, p: 4 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 6 }}>
          Core Capabilities
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderTop: `4px solid ${feature.color}`,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: feature.color === '#B31B1B' 
                      ? '0 12px 32px rgba(179,27,27,0.2)' 
                      : '0 12px 32px rgba(0,51,102,0.2)'
                  }
                }}
                onClick={() => navigate(feature.link)}
              >
                <CardContent sx={{ flexGrow: 1, p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ color: feature.color, mr: 2 }}>
                      {feature.icon}
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ color: '#003366', fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 600 }}>
                        {feature.subtitle}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                    {feature.description}
                  </Typography>

                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#003366', mb: 1 }}>
                    Key Benefits:
                  </Typography>
                  <List dense>
                    {feature.benefits.map((benefit, i) => (
                      <ListItem key={i} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon sx={{ fontSize: 20, color: feature.color }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={benefit}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip 
                      label={feature.roi} 
                      sx={{ 
                        bgcolor: feature.color === '#B31B1B' ? 'rgba(179,27,27,0.1)' : 'rgba(0,51,102,0.1)',
                        color: feature.color,
                        fontWeight: 600
                      }}
                    />
                    <Button
                      variant="text"
                      sx={{ 
                        color: feature.color,
                        fontWeight: 600,
                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(feature.link);
                      }}
                    >
                      Learn More →
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        </Paper>
      </Container>

      {/* Additional Features Section */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 2 }}>
            Additional Features
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
            Built for government, designed for excellence
          </Typography>

          <Grid container spacing={3}>
            {additionalFeatures.map((feature, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Card sx={{ 
                  p: 3, 
                  height: '100%',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3
                  }
                }}>
                  {feature.icon}
                  <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 2 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ROI Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: '#003366' }}>
            Proven ROI
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
            Real savings for federal agencies
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Card sx={{ p: 3, textAlign: 'center', bgcolor: '#e3f2fd' }}>
              <Typography variant="h3" sx={{ color: '#003366', fontWeight: 700 }}>
                $768M
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Annual Savings Potential
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ p: 3, textAlign: 'center', bgcolor: '#ffebee' }}>
              <Typography variant="h3" sx={{ color: '#B31B1B', fontWeight: 700 }}>
                7,580%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Year 1 ROI
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ p: 3, textAlign: 'center', bgcolor: '#e3f2fd' }}>
              <Typography variant="h3" sx={{ color: '#003366', fontWeight: 700 }}>
                85%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Compliance Cost Reduction
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ p: 3, textAlign: 'center', bgcolor: '#ffebee' }}>
              <Typography variant="h3" sx={{ color: '#B31B1B', fontWeight: 700 }}>
                70%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Faster Data Discovery
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: '#003366', color: 'white', py: 8 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'white' }}>
            Ready to Transform Your Data Operations?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', color: 'rgba(255,255,255,0.9)' }}>
            Join federal agencies already benefiting from E-Unify's data intelligence platform
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: 'white',
                color: '#003366',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: '#f8f9fa'
                }
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{
                borderColor: 'white',
                borderWidth: 2,
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'white',
                  borderWidth: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.15)'
                }
              }}
            >
              Contact Sales
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Capabilities;
